import { skipToken, useQuery } from "@tanstack/react-query";
import { z } from "zod/v4";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { roadsenseLivePayloadQuery } from "@visioni/a3s-reports/app";
import {
  RoadSensePayload,
  RoadSenseLivePayloadResponse,
  getRoadsenseLivePayloadEndpoint,
} from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";
import {
  liveResponseError,
  RoadSenseReportNotFoundError,
  RoadSenseReportNotReadyError,
  visibleReportError,
} from "./roadsense-report-errors";

export { RoadSenseReportNotFoundError, RoadSenseReportNotReadyError } from "./roadsense-report-errors";

/**
 * RoadSense report fetch hook — an **independent** implementation, deliberately
 * not shared with `features/wastesense`.
 *
 * Reports are fetched by **`reportId`** (a ULID), not `periodStart`: RoadSense
 * reports are cumulative from an asset's hire start, so two reports can share a
 * `periodStart` and differ only in `periodEnd`. See the identity note in
 * `get-report.ts`.
 *
 * Reads the **live payload** route (`…/by-id/{reportId}/sites/{siteId}/live`),
 * which the server builds on request so the screen includes today's counts.
 */

const STALE_TIME_MS = 60_000;

// The edge uploads traffic counts hourly, and every request re-reads the whole
// lifecycle from DynamoDB, so polling faster than every few minutes buys no
// fresher data and multiplies the read cost.
const POLL_INTERVAL_MS = 5 * 60_000;

/**
 * The live response with the payload left undecoded, so a payload that fails
 * the render schema still yields the workbook key (the screen offers the
 * download even when it can't render) — see `decodePayload`.
 */
const LiveEnvelope = z.object({
  data: RoadSenseLivePayloadResponse.shape.data.extend({ payload: z.unknown() }),
});

export interface UseRoadSenseReportResult {
  readonly payload: RoadSensePayload | undefined;
  /** ISO instant the server computed the payload at — the report's "last updated" time. */
  readonly asOf: string | undefined;
  /** This site's `.xlsx` key; `undefined` when the report has no workbook yet. */
  readonly workbookS3Key: string | undefined;
  /**
   * The last day the stored `.xlsx` covers (`YYYY-MM-DD`). The live report runs
   * to today, but the workbook is only rebuilt by the daily run, so it lags.
   * `undefined` when unknown (no workbook, or one being rebuilt).
   */
  readonly workbookPeriodEnd: string | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
}

/**
 * Fetches and polls a single RoadSense **site's** live report by
 * `(reportId, siteId)`, decoding its payload at the boundary.
 */
export function useRoadSenseReport(reportId: string | undefined, siteId: string | undefined): UseRoadSenseReportResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  // Reuse the endpoint's query key, but run our own queryFn to surface the typed 404/409.
  const queryKey =
    reportId !== undefined && siteId !== undefined ?
      roadsenseLivePayloadQuery(apiConfig, { type: "roadsense", reportId, siteId }).queryKey
    : [];

  const enabled = !!tenantId && reportId !== undefined && siteId !== undefined;

  const query = useQuery({
    queryKey,
    queryFn:
      apiConfig.tenant && reportId !== undefined && siteId !== undefined ?
        ({ signal }) => fetchLivePayload(apiConfig, reportId, siteId, signal)
      : skipToken,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Polls through a 409 too: a report whose first generation is still
    // running becomes available without a reload.
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      // 404/409 are terminal for this attempt (the next poll asks again).
      // Other errors keep the default backoff (3 attempts).
      if (error instanceof RoadSenseReportNotFoundError) return false;
      if (error instanceof RoadSenseReportNotReadyError) return false;
      return failureCount < 3;
    },
  });

  const data = query.data;
  const error = visibleReportError({ hasReport: data !== undefined, error: query.error });

  return {
    payload: data ? decodePayload(data.data.payload) : undefined,
    asOf: data?.data.asOf,
    workbookS3Key: data?.data.workbookS3Key,
    workbookPeriodEnd: data?.data.workbookPeriodEnd,
    isLoading: query.isLoading,
    isError: error !== null,
    error,
    isTenantPending: isTenantLoading || !tenantId,
  };
}

async function fetchLivePayload(
  apiConfig: ApiConfig,
  reportId: string,
  siteId: string,
  signal: AbortSignal | undefined,
): Promise<z.output<typeof LiveEnvelope>> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    // Unreachable in practice (gated by `enabled`/`skipToken`); the type system
    // doesn't know that.
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getRoadsenseLivePayloadEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId, siteId },
  });
  const response = await authenticatedFetch(`${tenant.apiBaseUrl}${path}`, signal ? { signal } : {});
  const error = await liveResponseError(response);
  if (error !== undefined) throw error;
  const body: unknown = await response.json();
  return LiveEnvelope.parse(body);
}

function decodePayload(payload: unknown): RoadSensePayload | undefined {
  const result = RoadSensePayload.safeParse(payload);
  if (result.success) return result.data;
  // The server validates the payload it builds, so this only fires on schema
  // drift between the deployed API and this client. Returning `undefined` lets
  // the renderer show an explicit "payload schema mismatch" state rather than
  // crashing.
  console.error("RoadSensePayload schema mismatch", result.error);
  return undefined;
}
